import React, { useEffect, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from '../../common/DataTable';
import PersonaBadge from '../../common/PersonaBadge';
import api from '../../../services/api';

interface Props {
  manifestId: string;
}

interface LinkRow {
  id: string;
  node_path: string;
  link_type: string;
  persona_name: string;
  persona_id: string;
  persona_revision?: number;
  created_at: string;
}

const columns: ColumnDef<LinkRow, any>[] = [
  { accessorKey: 'node_path', header: 'Node Path', cell: ({ getValue }) => <span className="font-mono text-xs">{getValue() as string}</span> },
  { accessorKey: 'link_type', header: 'Link Type', size: 100 },
  {
    accessorKey: 'persona_name',
    header: 'Persona',
    cell: ({ row }) => (
      <PersonaBadge personaId={row.original.persona_id} personaName={row.original.persona_name} size="sm" />
    ),
  },
  { accessorKey: 'persona_revision', header: 'Rev', size: 50 },
  {
    accessorKey: 'created_at',
    header: 'Date',
    cell: ({ getValue }) => new Date(getValue() as string).toLocaleDateString(),
  },
];

const ProvenanceLinks: React.FC<Props> = ({ manifestId }) => {
  const { darkMode } = useTheme();
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/cross-system/links/artifact/${manifestId}`)
      .then(r => {
        const data = r.data || [];
        setLinks(data.map((l: any) => ({
          id: l.id,
          node_path: l.metadata?.node_path || l.source_id?.slice(0, 8) || 'unknown',
          link_type: l.link_type,
          persona_name: l.metadata?.persona_name_at_creation || 'Unknown',
          persona_id: l.target_id,
          persona_revision: l.metadata?.persona_revision,
          created_at: l.created_at,
        })));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [manifestId]);

  return (
    <div className={`rounded-xl border p-5 ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
      <h3 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        Cross-System Links ({links.length})
      </h3>
      <DataTable
        columns={columns}
        data={links}
        isLoading={loading}
        searchable
        searchPlaceholder="Search by path or persona..."
        emptyMessage="No provenance links found. Links are created when nodes are hydrated."
        pageSize={15}
      />
    </div>
  );
};

export default ProvenanceLinks;
